import { z } from 'zod';

/**
 * Zod for BP-11 §5's two client routes and the mobile config
 * (ARCHITECTURE §3 Validation).
 *
 * Hand-written for the reason `features/licensing/schemas.ts` gives:
 * `lib/api/schema.d.ts` is regenerated in CI, and a screen compiled
 * against it would track whatever was last exported rather than the
 * contract this feature was written to.
 *
 * **There is no `token` field anywhere below, and that is the point.** A
 * push token is a live handle on somebody's handset (§9 calls it an
 * identifier and masks it in logs), so the api answers with a six-character
 * tail. The screen shows "Pixel 8 · …a3f91c", which is enough for a person
 * to recognise their own phone and useless to anybody else.
 */

/** §3's `push_device.platform`. */
export const devicePlatforms = ['android', 'ios'] as const;
export type DevicePlatform = (typeof devicePlatforms)[number];

/** Rule R6's three reasons a device stopped receiving. */
export const disabledReasons = ['unregistered', 'invalid', 'user'] as const;
export type DisabledReason = (typeof disabledReasons)[number];

/** §5's `DeviceOut`. */
export const deviceSchema = z.object({
  id: z.string(),
  platform: z.enum(devicePlatforms),
  token_tail: z.string(),
  app_version: z.string().nullable(),
  locale: z.string().nullable(),
  last_seen_at: z.string().nullable(),
  disabled_at: z.string().nullable(),
  disabled_reason: z.enum(disabledReasons).nullable(),
});
export type Device = z.infer<typeof deviceSchema>;

export const deviceListSchema = z.array(deviceSchema);

/** §5's `MobileConfigOut`. */
export const mobileConfigSchema = z.object({
  min_supported_version: z.string(),
  api_base: z.string(),
  features: z.object({ push: z.boolean(), try_on: z.boolean() }),
});
export type MobileConfig = z.infer<typeof mobileConfigSchema>;

/** Whether this device is one rule R7 would fan a reminder out to. */
export function isActive(device: Device): boolean {
  return device.disabled_at === null;
}
